//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
//------------------------------------------------------------------------------
// Helpers ----------------------------------------------------------------
import { getValue } from '@helpers/localStorage';
import { createCommissionDetailsReportCsv } from '@helpers/csv';
import { createCommissionDetailsReportPdf } from '@helpers/pdf';
import { StoreConfigContext } from '@helpers/storeConfigContext';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Button, ButtonStyle, Modal } from '@cmp/common';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { useGet } from '@api';
import { loadProviders } from '@api/endpoints/get/providers';
//------------------------------------------------------------------------------
// Constants -------------------------------------------------------------------
import { refCode } from '@helpers/constants/localStorage';
import { PawLoadingUrl } from '../../images';
//------------------------------------------------------------------------------
// React Component -----------------------------------------------------------------
const CommissionDetailButton = ({
  className,
  icon,
  style,
  startDate,
  endDate,
  isShort = false,
}) => {
  const { t, i18n } = useTranslation();
  const { storeConfig } = useContext(StoreConfigContext);
  const componentClasses = classNames(styles.CommissionDetailButton, {
    [className]: className,
    [styles.CommissionDetailButtonShort]: isShort,
  });
  const [shouldCreateReport, setShouldCreateReport] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [provider, setProvider] = useState(null);
  const [downloadButton, setDownloadButton] = useState('');

  const referralCode = getValue(refCode);

  const { data, loading, error, refetch } = useGet({
    request: {
      url: 'payments/commission-payments',
    },
    params: {
      referralCode,
      'store-id': import.meta.env.VITE_STORE_ID,
      startDate:
        startDate
          ?.clone()
          .utc(true)
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .toISOString() || undefined,
      endDate:
        endDate
          ?.clone()
          .utc(true)
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .add(1, 'd')
          .toISOString() || undefined,
    },
    skip: true,
  });

  useEffect(() => {
    loadProviders({ showAccountInfo: true }).then((result) => {
      setProvider(result.data.accountInfo);
    });
  }, []);
  useEffect(() => {
    if (!loading && shouldCreateReport) {
      if (!error && data && data?.data.length > 0) {
        if (downloadButton === 'csv') {
          if (!provider) return;
          createCommissionDetailsReportCsv(data, storeConfig, provider, t);
        } else if (downloadButton === 'pdf') {
          createCommissionDetailsReportPdf(data, storeConfig, t);
        }
        setShouldCreateReport(false);
        setDownloadButton('');
      }
      if (error || data?.data.length === 0) {
        setShouldCreateReport(false);
        setErrorModalOpen(true);
        setDownloadButton('');
      }
    }
  }, [loading, error, data, provider]);

  return (
    <>
      {loading && (
        <div className={styles.LoadingBackdrop}>
          <div className={styles.LoadingContainer}>
            <img className={styles.LoadingImage} src={PawLoadingUrl(i18n)} />
            <h3 className={styles.LoadingText}>
              {t('processing_your_request')}
            </h3>
          </div>
        </div>
      )}
      <Button
        className={componentClasses}
        style={style ? style : ButtonStyle.Primary}
        onClick={() => {
          refetch();
          setShouldCreateReport(true);
          setDownloadButton('csv');
        }}
        iconComponent={
          icon ? (
            <img
              alt={t('accessibility_SearchProducts')}
              src={icon}
              className={styles.Icon}
            />
          ) : (
            <StripeIcon />
          )
        }
      >
        {t('profile_CommissionDetailsReport')}
      </Button>
      <Button
        className={componentClasses}
        style={style ? style : ButtonStyle.Primary}
        onClick={() => {
          refetch();
          setShouldCreateReport(true);
          setDownloadButton('pdf');
        }}
        iconComponent={
          icon ? (
            <img
              alt={t('accessibility_SearchProducts')}
              src={icon}
              className={styles.Icon}
            />
          ) : (
            <StripeIcon />
          )
        }
      >
        {t('profile_CommissionDetailsReportPdf')}
      </Button>
      {errorModalOpen && (
        <FocusTrap>
          <Modal.Generic
            title={t('commissionDetailsReport_modal_title')}
            subtitle={t('commissionDetailsReport_modal_body')}
            action={t('commissionDetailsReport_modal_ok')}
            onClick={() => setErrorModalOpen(false)}
            onClose={() => setErrorModalOpen(false)}
          />
        </FocusTrap>
      )}
    </>
  );
};
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default CommissionDetailButton;
